import type { useTranslation } from "react-i18next";
import type { useLocationParCounts } from "../../utils/warningDialogs/useLocationParCounts";
import { MetaChip } from "../UIToolkit/Badges/MetaChip";
import type { DropdownItem } from "../UIToolkit/Inputs/SystemDropDown/types";

type TFn = ReturnType<typeof useTranslation>["t"];
type ParCountsMap = ReturnType<ReturnType<typeof useLocationParCounts>>;

// Sentinel values for the two presentational section headers (rendered via SystemDropdown's single-select
// group bands). Namespaced so they can never collide with a real location name.
export const WORKS_MOST_GROUP = "__location-picker-works-most__";
export const OTHER_GROUP = "__location-picker-other__";

export type ItemBuildArgs = {
	counts: Record<string, number> | undefined;
	parCounts: ParCountsMap;
	nurseId: number | null | undefined;
	currentLocation: string | undefined;
	t: TFn;
	dataTestId: string | undefined;
};

// DEFAULT: the history map only carries locations the nurse actually worked, so an absent location
// legitimately means zero recent shifts (also the correct reading while the request is loading or
// disabled) — it is not missing/invalid data being papered over.
export const recentCount = (name: string, nurseId: number | null | undefined, counts: Record<string, number> | undefined): number =>
	nurseId ? (counts?.[name] ?? 0) : 0;

const buildParPill = (name: string, parCounts: ParCountsMap, t: TFn, dataTestId: string | undefined) => {
	const par = parCounts[name];
	const testId = dataTestId ? `${dataTestId}-par-${name}` : undefined;
	if (!par?.hasPar) {
		return <MetaChip label={t("locationPicker.noPar")} tone="neutral" dataTestId={testId} />;
	}
	return <MetaChip label={`${par.current}/${par.par}`} tone={par.belowPar ? "error" : "neutral"} dataTestId={testId} />;
};

const buildLocationItem = (name: string, args: ItemBuildArgs): DropdownItem<string> => {
	const { counts, parCounts, nurseId, currentLocation, t, dataTestId } = args;
	const recent = recentCount(name, nurseId, counts);
	const isCurrent = !!currentLocation && name === currentLocation;
	return {
		value: name,
		id: name,
		label: name,
		secondaryLabel: nurseId && recent > 0 ? t("locationPicker.recentShifts", { count: recent }) : undefined,
		startAdornment: isCurrent ? (
			<MetaChip label={t("locationPicker.current")} tone="primary" dataTestId={dataTestId ? `${dataTestId}-current-badge` : undefined} />
		) : undefined,
		endAdornment: buildParPill(name, parCounts, t, dataTestId),
	};
};

// Below-par first, then most recently worked, then by name — applied within each section.
const rankLocations = (a: string, b: string, args: ItemBuildArgs): number => {
	const { parCounts, nurseId, counts } = args;
	const belowA = parCounts[a]?.belowPar ? 1 : 0;
	const belowB = parCounts[b]?.belowPar ? 1 : 0;
	if (belowA !== belowB) return belowB - belowA;
	const recentDelta = recentCount(b, nurseId, counts) - recentCount(a, nurseId, counts);
	if (recentDelta !== 0) return recentDelta;
	return a.localeCompare(b);
};

/**
 * Build the ordered dropdown items for the location picker. Current location pinned first (always shown);
 * with a nurse, the rest split into a "works most" section (recent shifts > 0) and "other locations", each
 * ranked below-par first, then most-worked, then by name. With no nurse it degrades to a flat list in the
 * department's own location order — still par-pilled, current still pinned first.
 */
export const buildLocationItems = (uniqueNames: string[], nurseFirstName: string | undefined, args: ItemBuildArgs): DropdownItem<string>[] => {
	const { nurseId, currentLocation, counts, t } = args;
	const currentItem = currentLocation ? buildLocationItem(currentLocation, args) : null;
	const rest = uniqueNames.filter((name) => name !== currentLocation);

	if (!nurseId) {
		return [...(currentItem ? [currentItem] : []), ...rest.map((name) => buildLocationItem(name, args))];
	}

	const sorted = [...rest].sort((a, b) => rankLocations(a, b, args));
	const worked = sorted.filter((name) => recentCount(name, nurseId, counts) > 0);
	const other = sorted.filter((name) => recentCount(name, nurseId, counts) === 0);

	const worksMostLabel = nurseFirstName ? t("locationPicker.worksMost", { name: nurseFirstName }) : t("locationPicker.worksMostGeneric");
	const worksMostHeader: DropdownItem<string> = { value: WORKS_MOST_GROUP, id: WORKS_MOST_GROUP, label: worksMostLabel, groupValues: worked };
	const otherHeader: DropdownItem<string> = { value: OTHER_GROUP, id: OTHER_GROUP, label: t("locationPicker.otherLocations"), groupValues: other };

	return [
		...(currentItem ? [currentItem] : []),
		...(worked.length ? [worksMostHeader, ...worked.map((name) => buildLocationItem(name, args))] : []),
		...(other.length ? [otherHeader, ...other.map((name) => buildLocationItem(name, args))] : []),
	];
};
